import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Smile,
  Palette,
  Type,
  Download,
  BarChart3,
  Layers,
  Zap,
  Brain,
  Image,
  Wand2,
  ArrowRight,
} from "lucide-react";

const Features = () => {
  const features = [
    {
      icon: Sparkles,
      title: "AI Thumbnail Generation",
      description: "Generate stunning thumbnails in seconds using our advanced AI models trained on millions of high-performing YouTube thumbnails.",
      highlight: true,
    },
    {
      icon: Smile,
      title: "Face Expression Enhancement",
      description: "Automatically enhance facial expressions to maximize emotional impact. Our AI knows what makes viewers click.",
    },
    {
      icon: Palette,
      title: "Emotion & Style Presets",
      description: "Choose from dozens of emotion presets: exciting, mysterious, urgent, educational, and more. Perfect styling every time.",
    },
    {
      icon: Type,
      title: "Text Overlay Optimization",
      description: "AI-optimized text placement and styling. The right words in the right place for maximum impact.",
    },
    {
      icon: Download,
      title: "High-Resolution Export",
      description: "Export in full HD and 4K resolution. Perfect quality for any display, from mobile to desktop.",
    },
    {
      icon: BarChart3,
      title: "A/B Testing Thumbnails",
      description: "Generate multiple variations and test which performs best. Data-driven decisions for better CTR.",
    },
    {
      icon: Layers,
      title: "Branding Consistency",
      description: "Save your brand colors, fonts, and styles. Maintain consistent branding across all your thumbnails.",
    },
    {
      icon: Zap,
      title: "Instant Generation",
      description: "Get your thumbnails in under 30 seconds. No waiting, no queues, just instant results.",
    },
    {
      icon: Brain,
      title: "Smart Suggestions",
      description: "AI analyzes your title and suggests the best styles, emotions, and layouts for maximum engagement.",
    },
    {
      icon: Image,
      title: "Background Removal",
      description: "Automatically remove and replace backgrounds. Create professional composites without Photoshop.",
    },
    {
      icon: Wand2,
      title: "One-Click Enhancements",
      description: "Enhance colors, contrast, and sharpness with a single click. Professional results, zero effort.",
    },
    {
      icon: Sparkles,
      title: "Template Library",
      description: "Access hundreds of proven thumbnail templates. Customize to match your style and niche.",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-24">
        {/* Hero */}
        <section className="section-padding relative overflow-hidden">
          <div className="absolute inset-0 hero-glow" />
          <div className="container-custom relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <span className="text-sm font-semibold text-brand-purple uppercase tracking-wider mb-4 block">
                Features
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Everything You Need to Create{" "}
                <span className="gradient-text">Winning Thumbnails</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Powerful AI features designed specifically for YouTube creators. 
                No design skills required.
              </p>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className={`relative p-8 rounded-2xl border transition-all duration-300 group hover:border-primary/30 ${
                    feature.highlight
                      ? "bg-gradient-to-br from-primary/10 to-card border-primary/30"
                      : "bg-card border-border"
                  }`}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl" />
                  
                  <div className="relative z-10">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${
                      feature.highlight ? "gradient-bg" : "bg-primary/10"
                    }`}>
                      <feature.icon className={`w-7 h-7 ${
                        feature.highlight ? "text-primary-foreground" : "text-brand-purple"
                      }`} />
                    </div>

                    <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto text-center p-12 rounded-3xl bg-card border border-border relative overflow-hidden">
              <div className="absolute inset-0 hero-glow opacity-50" />
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Ready to Try These Features?
                </h2>
                <p className="text-muted-foreground mb-8">
                  Start creating professional thumbnails today. No credit card required.
                </p>
                <Link to="/signup">
                  <Button variant="hero" size="xl" className="group">
                    Get Started Free
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Features;
